import { faEnvelopeBadge } from '@fortawesome/pro-solid-svg-icons/faEnvelopeBadge';
import { AxiosResponse, referralsSendEmail, useClientAccountOverview } from '@ukpostbox/client-data';
import { ReferralsSendEmailType } from '@ukpostbox/client-data/lib/Types/ReferralTypes';
import {
	ButtonSubmit,
	Colour,
	ColourStateSolid,
	EmailControl,
	FormWrapperLabel,
	Modal,
	ModalType,
	useNotifications,
	useTranslation,
	ValidationPatterns,
} from '@ukpostbox/frontend-framework';
import { Controller, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useEffect, useState } from 'react';
import { ErrorMapper } from '../../../../_Global/Components/ErrorMapper';

interface SendEmailModalType extends ModalType {
	emailAddress?: 'string';
	subject?: 'string';
	recipientName?: 'string';
	message?: 'string';
	clientName?: 'string';
}

function SendEmailModal(props: SendEmailModalType) {
	const { t } = useTranslation();
	const { data } = useClientAccountOverview();
	const { AddNotification } = useNotifications();

	const [inviteError, setInviteError] = useState<AxiosResponse>();
	const [inviteSending, setInviteSending] = useState<boolean>(false);

	const formMethods = useForm({
		defaultValues: {
			emailAddress: props.emailAddress,
			subject: props.subject,
			recipientName: props.recipientName,
			message: props.message,
			clientName: props.clientName,
		},
		mode: 'onChange',
	});

	const { handleSubmit, register, getValues, reset, formState } = formMethods;

	const HandleEmailSubmit = (formData: any) => {
		setInviteSending(true);

		const newData: ReferralsSendEmailType = {
			...formData,
			clientName: data?.name,
		};

		referralsSendEmail(newData)
			.then((r) => {
				AddNotification({
					icon: faEnvelopeBadge,
					title: 'Success!',
					description: 'Your invite was sent',
					colour: Colour.Success,
				});
				props.closeCallback();
			})
			.catch((e: any) => {
				setInviteError(e);
			})
			.finally(() => {
				setInviteSending(false);
			});
	};

	useEffect(() => {
		reset();
	}, [props.isOpen]);

	return (
		<Modal title={t('settings.referralsSendEmail.title')} isOpen={props.isOpen} closeCallback={props.closeCallback}>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(HandleEmailSubmit)}>
					<fieldset className='flex-column flex-gap-md'>
						{inviteError?.data && <ErrorMapper errorResponse={inviteError} />}

						<FormWrapperLabel
							label={t('settings.referralsSendEmail.labels.recipientName')}
							invalid={formState.errors.recipientName && formState.errors.recipientName?.message !== ''}
							validationMessage={formState.errors.recipientName?.message as string}
						>
							<input
								type='text'
								aria-invalid={formState.errors.recipientName ? 'true' : 'false'}
								{...register('recipientName', {
									required: t('validation.required'),
									minLength: { value: 2, message: t('validation.tooShort') },
								})}
							/>
						</FormWrapperLabel>

						<Controller
							control={formMethods.control}
							name='emailAddress'
							defaultValue={props.emailAddress}
							rules={{
								required: true,
								pattern: { value: ValidationPatterns.email, message: `Format is not valid` },
							}}
							render={({ field, fieldState }) => (
								<FormWrapperLabel
									label='E-mail'
									invalid={fieldState.invalid}
									validationMessage={fieldState.error?.message}
								>
									<EmailControl
										name='emailAddress'
										placeholder=''
										defaultValue={props.emailAddress}
										onChangeCallback={(val) => field.onChange(val)}
									/>
								</FormWrapperLabel>
							)}
						/>

						<FormWrapperLabel
							label={t('settings.referralsSendEmail.labels.subject')}
							invalid={formState.errors.subject && formState.errors.subject?.message !== ''}
							validationMessage={formState.errors.subject?.message as string}
						>
							<input
								type='text'
								aria-invalid={formState.errors.subject ? 'true' : 'false'}
								{...register('subject', {
									required: t('validation.required'),
									minLength: { value: 2, message: t('validation.tooShort') },
								})}
							/>
						</FormWrapperLabel>

						<FormWrapperLabel
							label={t('settings.referralsSendEmail.labels.message')}
							invalid={formState.errors.message && formState.errors.message?.message !== ''}
							validationMessage={formState.errors.message?.message as string}
						>
							<textarea
								rows={10}
								aria-invalid={formState.errors.message ? 'true' : 'false'}
								{...register('message', {
									required: t('validation.required'),
									minLength: { value: 2, message: t('validation.tooShort') },
								})}
							/>
						</FormWrapperLabel>

						<ButtonSubmit
							title={t('settings.referralsSendEmail.actions.send')}
							colour={ColourStateSolid.Primary}
							fullWidth
							active={inviteSending}
							disabled={inviteSending || !formState.isValid || formState.isSubmitting}
						/>
					</fieldset>
				</form>
			</FormProvider>
		</Modal>
	);
}

export default SendEmailModal;
